/**
 * Shared vanishing-point math primitives.
 *
 * Used by both `cameraSolver.ts` (main thread, no `cv` import) and the
 * auto-align worker (web worker). Pure TypeScript with zero dependencies
 * so the same kernel runs in either context.
 *
 * Conventions:
 *   - Image coordinates are in pixels with origin at the top-left,
 *     X right, Y down. The principal point is at (cx, cy) = (W/2, H/2).
 *   - Homogeneous lines are normalized so (a, b) is a unit vector and
 *     a·x + b·y + c is the signed perpendicular pixel distance from
 *     point (x, y) to the supporting line.
 */

export type Vec2 = [number, number];
export type Vec3 = [number, number, number];
export type Mat3 = [
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
];

// ---------------------------------------------------------------------------
// Homogeneous lines
// ---------------------------------------------------------------------------

/**
 * Build a normalized homogeneous line through two image points.
 *
 * Returns (a, b, c) such that a·x + b·y + c = 0 lies on the line, with
 * (a, b) a unit normal so the perpendicular distance from any point is just
 * |a·px + b·py + c|.
 */
export function homogeneousLine(p1: Vec2, p2: Vec2): Vec3 {
  const a = p1[1] - p2[1];
  const b = p2[0] - p1[0];
  const c = p1[0] * p2[1] - p2[0] * p1[1];
  const norm = Math.hypot(a, b);
  if (norm < 1e-12) return [0, 0, 0];
  return [a / norm, b / norm, c / norm];
}

/** Perpendicular pixel distance from a point to a normalized homogeneous line. */
export function pointLineDistance(line: Vec3, point: Vec2): number {
  return Math.abs(line[0] * point[0] + line[1] * point[1] + line[2]);
}

// ---------------------------------------------------------------------------
// 3×3 symmetric eigendecomposition (Jacobi)
// ---------------------------------------------------------------------------

/**
 * Cyclic Jacobi eigendecomposition of a 3×3 symmetric matrix.
 *
 * Returns three eigenvalues `evals` and three orthonormal eigenvectors as the
 * columns of `evecs`. Determinism: zero allocations after the initial setup,
 * fixed iteration count, no random rotations.
 *
 * For our 3×3 use case the off-diagonals shrink quadratically each sweep, so
 * 12 sweeps converges to ~1e-15 even for ill-conditioned inputs.
 */
function jacobiEigen3x3(m: Mat3): { evals: Vec3; evecs: Mat3 } {
  const a: Mat3 = [m[0], m[1], m[2], m[3], m[4], m[5], m[6], m[7], m[8]];
  const v: Mat3 = [1, 0, 0, 0, 1, 0, 0, 0, 1];

  for (let sweep = 0; sweep < 24; sweep++) {
    const off = Math.abs(a[1]) + Math.abs(a[2]) + Math.abs(a[5]);
    if (off < 1e-15) break;

    for (let p = 0; p < 2; p++) {
      for (let q = p + 1; q < 3; q++) {
        const apq = a[p * 3 + q];
        if (Math.abs(apq) < 1e-18) continue;
        const app = a[p * 3 + p];
        const aqq = a[q * 3 + q];
        const theta = (aqq - app) / (2 * apq);
        let t: number;
        if (Math.abs(theta) > 1e15) {
          t = 1 / (2 * theta);
        } else {
          t =
            Math.sign(theta) / (Math.abs(theta) + Math.sqrt(1 + theta * theta));
          if (theta === 0) t = 1;
        }
        const c = 1 / Math.sqrt(1 + t * t);
        const s = t * c;

        // Apply the rotation to A: A' = JᵀAJ.
        a[p * 3 + p] = app - t * apq;
        a[q * 3 + q] = aqq + t * apq;
        a[p * 3 + q] = 0;
        a[q * 3 + p] = 0;

        for (let r = 0; r < 3; r++) {
          if (r === p || r === q) continue;
          const arp = a[r * 3 + p];
          const arq = a[r * 3 + q];
          a[r * 3 + p] = c * arp - s * arq;
          a[p * 3 + r] = a[r * 3 + p];
          a[r * 3 + q] = s * arp + c * arq;
          a[q * 3 + r] = a[r * 3 + q];
        }

        // Accumulate the rotation into V.
        for (let r = 0; r < 3; r++) {
          const vrp = v[r * 3 + p];
          const vrq = v[r * 3 + q];
          v[r * 3 + p] = c * vrp - s * vrq;
          v[r * 3 + q] = s * vrp + c * vrq;
        }
      }
    }
  }

  return {
    evals: [a[0], a[4], a[8]],
    evecs: v,
  };
}

/**
 * Eigenvector of a 3×3 symmetric matrix corresponding to its smallest
 * eigenvalue. Used for the homogeneous LS vanishing-point fit.
 */
function smallestEigenvector(m: Mat3): Vec3 {
  const { evals, evecs } = jacobiEigen3x3(m);
  let minIdx = 0;
  let minVal = Math.abs(evals[0]);
  for (let i = 1; i < 3; i++) {
    const v = Math.abs(evals[i]);
    if (v < minVal) {
      minVal = v;
      minIdx = i;
    }
  }
  return [evecs[minIdx], evecs[3 + minIdx], evecs[6 + minIdx]];
}

// ---------------------------------------------------------------------------
// Weighted least-squares vanishing point
// ---------------------------------------------------------------------------

export interface WeightedLine {
  /** Normalized homogeneous form (a, b, c) with a² + b² = 1. */
  line: Vec3;
  /** Length-based weight; lines with more pixel support count more. */
  weight: number;
}

/**
 * Compute the vanishing point that minimizes the weighted sum of squared
 * perpendicular distances `Σ wᵢ (ℓᵢ · v)²` over the inlier lines, with
 * `‖v‖ = 1`. The minimizer is the eigenvector of `M = Σ wᵢ ℓᵢ ℓᵢᵀ` with
 * the smallest eigenvalue.
 *
 * Returns null if fewer than 2 lines are supplied or the eigendecomposition
 * is degenerate. Returns a finite point in pixels when the VP is finite, or
 * a direction vector with `[*, *, 0]` semantically encoding "VP at infinity"
 * — callers detect that case via `isFinite(vp[0])`.
 */
export function svdVanishingPoint(lines: WeightedLine[]): Vec2 | null {
  if (lines.length < 2) return null;

  let m00 = 0,
    m01 = 0,
    m02 = 0;
  let m11 = 0,
    m12 = 0;
  let m22 = 0;

  for (const { line, weight } of lines) {
    const w = weight;
    const a = line[0];
    const b = line[1];
    const c = line[2];
    m00 += w * a * a;
    m01 += w * a * b;
    m02 += w * a * c;
    m11 += w * b * b;
    m12 += w * b * c;
    m22 += w * c * c;
  }

  const M: Mat3 = [m00, m01, m02, m01, m11, m12, m02, m12, m22];
  const v = smallestEigenvector(M);

  if (Math.abs(v[2]) < 1e-9) {
    // VP at infinity. Encode as a non-finite x sentinel; callers check
    // isFinite. The direction is (v[0], v[1]) up to sign.
    return [Number.POSITIVE_INFINITY, Number.POSITIVE_INFINITY];
  }

  return [v[0] / v[2], v[1] / v[2]];
}

// ---------------------------------------------------------------------------
// Rotation helpers — used by perspectiveRefine
// ---------------------------------------------------------------------------

/**
 * Build the world-to-camera rotation matrix for a (pitch, yaw) pair with
 * zero roll.
 *
 * PoseRef world axes: X right, Y forward, Z up.
 * Camera frame:       X right, Y up,      Z backward (camera looks along -Z).
 *
 * At (pitch=0, yaw=0) the camera looks along +world-Y with image-up
 * aligned to +world-Z. This requires a fixed base rotation:
 *
 *   R_base · (1, 0, 0) = (1, 0, 0)    (world right → camera right)
 *   R_base · (0, 1, 0) = (0, 0, -1)   (world forward → camera view direction)
 *   R_base · (0, 0, 1) = (0, 1, 0)    (world up → camera up)
 *
 * Yaw rotates around world-Z (gravity), pitch rotates around the camera's
 * X axis after yaw. Composed:
 *
 *   R(pitch, yaw) = Rx(pitch) · R_base · Rz(yaw)
 *
 * Closed form (with cp = cos(pitch), sp = sin(pitch), cy = cos(yaw), sy = sin(yaw)):
 *
 *     [  cy        -sy         0  ]
 *     [  sp·sy     sp·cy      cp  ]
 *     [ -cp·sy    -cp·cy      sp  ]
 *
 * The defining no-roll property is `R · (0, 0, 1) = (0, cp, sp)` — world
 * up always has zero camera-X component, so world-vertical lines always
 * project to image-vertical regardless of pitch or yaw.
 *
 * Sign convention: positive pitch tilts the camera *down* (looking at the
 * ground); positive yaw pans the camera to the *right* (towards +world-X).
 * `initialGuessFromHorizon` and the residual functions are written to match.
 *
 * Layout: row-major, indices 0..8 are r00 r01 r02 r10 r11 r12 r20 r21 r22.
 */
export function rotationFromPitchYaw(pitch: number, yaw: number): Mat3 {
  const cp = Math.cos(pitch);
  const sp = Math.sin(pitch);
  const cy = Math.cos(yaw);
  const sy = Math.sin(yaw);
  return [cy, -sy, 0, sp * sy, sp * cy, cp, -cp * sy, -cp * cy, sp];
}

/** Multiply a 3×3 matrix by a 3-vector (row-major). */
export function mat3MulVec3(m: Mat3, v: Vec3): Vec3 {
  return [
    m[0] * v[0] + m[1] * v[1] + m[2] * v[2],
    m[3] * v[0] + m[4] * v[1] + m[5] * v[2],
    m[6] * v[0] + m[7] * v[1] + m[8] * v[2],
  ];
}

/** Transpose a 3×3 matrix. */
export function mat3Transpose(m: Mat3): Mat3 {
  return [m[0], m[3], m[6], m[1], m[4], m[7], m[2], m[5], m[8]];
}
